import React, { useState, useEffect } from 'react';
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import Box from "@mui/material/Box";
import CustomerDropdown from "./CustomerDropdown";
import axiosInstance from "../AxiosInstance";
import OutputsDemandTable from "./OutputsDemandTable";
import {Skeleton, Paper, Grid} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {downloadCsv} from "../Utilities/csvUtility";

interface ForecastPeriod {
    forecastPeriodId: number;
    name?: string;
    periodIndex?: number;
}

interface ForecastItem {
    forecastId: number;
    startPeriod: number;
    forecastPeriods?: ForecastPeriod[];
}

interface OutputsSummaryData {
    periodicDemands: any[];
    weekToMonthMap: Record<string, number>;
}

const ForecastOutputsDemand = () => {
    const [forecasts, setForecasts] = useState<ForecastItem[]>([]);
    const [selectedForecastId, setSelectedForecastId] = useState<number | null>(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState<number | null>(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
    const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
    const [startPeriod, setStartPeriod] = useState<number>(1);
    const [summaryData, setSummaryData] = useState<OutputsSummaryData>({
        periodicDemands: [],
        weekToMonthMap: {},
    });
    const [loadingForecasts, setLoadingForecasts] = useState<boolean>(true);
    const [loadingTable, setLoadingTable] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [loadingCsv, setLoadingCsv] = useState<boolean>(false);

    const getToken = () => localStorage.getItem("token");

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem('selectedForecast') as string);
        const storedForecastPeriodId = localStorage.getItem('selectedForecastPeriodId');

        if (storedForecast?.forecastId) {
            setSelectedForecastId(storedForecast.forecastId);
            setStartPeriod(storedForecast.startPeriod);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        } else {
            setSelectedForecastPeriodId(null);
        }

        fetchForecastsAndPeriods();
    }, []);

    const fetchForecastsAndPeriods = async () => {
        try {
            setLoadingForecasts(true);
            const token = getToken();
            const response = await axiosInstance.get("/forecasts", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setForecasts(response.data as ForecastItem[]);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    useEffect(() => {
        if (selectedForecastId && selectedForecastPeriodId !== null) {
            const fetchDemandSummary = async () => {
                setLoadingTable(true);
                try {
                    const params = {
                        forecastPeriodId: selectedForecastPeriodId,
                        customerId: selectedCustomerId,
                        channelId: selectedChannelId
                    };
                    const response = await axiosInstance.get(`/forecasts/${selectedForecastId}/outputs-demand`, { params });
                    setSummaryData(response.data as OutputsSummaryData);
                } catch (error) {
                    console.error("Failed to fetch output demand:", error);
                    setSummaryData({ periodicDemands: [],
                        weekToMonthMap: {}, });
                } finally {
                    setLoadingTable(false);
                }
            };

            fetchDemandSummary();
        }
    }, [selectedForecastId, selectedForecastPeriodId, selectedCustomerId, selectedChannelId]);

    const selectedForecast = forecasts.find((f) => f.forecastId === selectedForecastId);

    const setSelectedForecastPeriod = (forecastPeriodId: number | null) => {
        if (forecastPeriodId !== null && forecastPeriodId !== undefined) {
            localStorage.setItem('selectedForecastPeriodId', String(forecastPeriodId));
            setSelectedForecastPeriodId(forecastPeriodId);
        } else {
            localStorage.removeItem('selectedForecastPeriodId');
            setSelectedForecastPeriodId(null);
        }
    };

    const handleForecastSelected = (forecastId: number | null) => {
        setSelectedForecastId(forecastId);
        localStorage.setItem('selectedForecast', JSON.stringify({ forecastId }));
        const forecast = forecasts.find(f => f.forecastId === forecastId);
        const sortedPeriods = forecast?.forecastPeriods
            ? [...forecast.forecastPeriods].sort((a, b) => {
                const aIdx = a.periodIndex ?? a.forecastPeriodId;
                const bIdx = b.periodIndex ?? b.forecastPeriodId;
                return aIdx - bIdx;
            })
            : [];
        const firstPeriodId = sortedPeriods.length > 0 ? sortedPeriods[0].forecastPeriodId : null;
        setSelectedForecastPeriod(firstPeriodId);
    };

    // Ensure that when a forecast year is selected but the period is unset or invalid,
    // we default to the first available period (ordered by periodIndex asc)
    useEffect(() => {
        if (!selectedForecastId || forecasts.length === 0) return;
        const forecast = forecasts.find(f => f.forecastId === selectedForecastId);
        const periods = forecast?.forecastPeriods || [];
        if (periods.length === 0) return;

        const periodExists = periods.some(fp => fp.forecastPeriodId === selectedForecastPeriodId);
        if (!periodExists) {
            const first = [...periods].sort((a, b) => {
                const aIdx = a.periodIndex ?? a.forecastPeriodId;
                const bIdx = b.periodIndex ?? b.forecastPeriodId;
                return aIdx - bIdx;
            })[0];
            setSelectedForecastPeriod(first ? first.forecastPeriodId : null);
        }
    }, [forecasts, selectedForecastId, selectedForecastPeriodId]);

    const handleDownloadCsv = async () => {
        try {
            const params = {
                forecastPeriodId: selectedForecastPeriodId ?? undefined,
                customerId: selectedCustomerId ?? undefined,
                channelId: selectedChannelId ?? undefined
            };
            const fileName = `OutputsDemand_${selectedForecastId}_${selectedForecastPeriodId}.csv`;

            await downloadCsv(`/forecasts/${selectedForecastId}/outputs-demand-csv`, params, fileName, setLoadingCsv, setError);
        } catch (error) {
            console.error("Unexpected error during CSV download:", error);
        }
    };

    const handleCustomerSelected = (selection: any) => {
        if (selection.type === 'channel') {
            setSelectedChannelId(selection.id);
            setSelectedCustomerId(null);
        } else if (selection.type === 'clear') {
            setSelectedCustomerId(null);
            setSelectedChannelId(null);
        } else {
            setSelectedCustomerId(selection.id);
            setSelectedChannelId(null);
        }
    };

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Paper elevation={2} sx={{ p: 2, mb: 1, flexShrink: 0 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastDropdown
                                forecasts={forecasts}
                                selectedForecastId={selectedForecastId}
                                onForecastSelected={handleForecastSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                    <ForecastPeriodDropdown
                        forecastPeriods={selectedForecast?.forecastPeriods || []}
                        selectedForecastPeriodId={selectedForecastPeriodId}
                        onForecastPeriodSelected={setSelectedForecastPeriod}
                        width={200}
                    />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <CustomerDropdown onCustomerSelected={handleCustomerSelected} channelSelectable={true} />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="circular" width={32} height={32} />
                        ) : (
                            <Tooltip title="Download CSV">
                                <span>
                                    <IconButton onClick={handleDownloadCsv} aria-label="download csv" disabled={loadingCsv}>
                                        {loadingCsv ? <CircularProgress size={24} /> : <FileDownloadIcon />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Grid>
                </Grid>
            </Paper>
            <Box sx={{ flex: 1, overflow: "hidden" }}>
                {loadingTable ? (
                    <Skeleton variant="rectangular" width="100%" height="100%" />
                ) : (
                    selectedForecastId &&
                    selectedForecastPeriodId !== null && (
                        <OutputsDemandTable summaryData={summaryData} />
                    )
                )}
            </Box>
        </Box>
    );
};

export default ForecastOutputsDemand;


